import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { DomainSkill } from '../../types/domain-skill';

/** The skill fields the combobox reads; DomainSkill and expert tip skills both fit */
export type SkillOption = Pick<DomainSkill, 'id' | 'title'> & { domain: { title: string } };

interface SkillsComboboxProps {
  skills: SkillOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  max?: number;
  error?: string;
  /** Hide the built-in "Skills (up to n)" label when the parent renders its own */
  hideLabel?: boolean;
}

export default function SkillsCombobox({
  skills,
  selectedIds,
  onChange,
  max = Infinity,
  error,
  hideLabel = false,
}: SkillsComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const openMenu = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
    setOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        (containerRef.current && containerRef.current.contains(target)) ||
        (menuRef.current && menuRef.current.contains(target))
      ) {
        return;
      }
      closeMenu();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, closeMenu]);

  const grouped = skills.reduce<Record<string, SkillOption[]>>((acc, skill) => {
    const key = skill.domain.title;
    if (!acc[key]) acc[key] = [];
    acc[key].push(skill);
    return acc;
  }, {});

  const filteredGroups = Object.entries(grouped).reduce<Record<string, SkillOption[]>>(
    (acc, [domain, domainSkills]) => {
      const filtered = domainSkills.filter((s) =>
        s.title.toLowerCase().includes(search.toLowerCase())
      );
      if (filtered.length > 0) acc[domain] = filtered;
      return acc;
    },
    {}
  );

  const selectedSkills = skills.filter((s) => selectedIds.includes(s.id));
  const atMax = selectedIds.length >= max;

  const toggleSkill = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((i) => i !== id));
    } else if (!atMax) {
      onChange([...selectedIds, id]);
      setSearch('');
      if (selectedIds.length + 1 >= max) closeMenu();
    }
  };

  const removeChip = (id: number) => {
    onChange(selectedIds.filter((i) => i !== id));
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeMenu();
  };

  return (
    <div ref={containerRef} className="relative">
      {!hideLabel && (
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mb-1">
          Skills <span className="text-pine-danger">*</span>
          <span className="ml-1 font-normal normal-case tracking-normal text-pine-ink-soft">
            (up to {max})
          </span>
        </p>
      )}

      <div
        ref={triggerRef}
        data-testid="skills-trigger"
        className={`min-h-[38px] flex flex-wrap gap-1.5 items-center border-[1.5px] rounded-pine-input px-2 py-1.5 bg-white cursor-text transition-colors ${
          error
            ? 'border-pine-danger'
            : open
              ? 'border-pine-pine'
              : 'border-pine-line hover:border-pine-pine'
        }`}
        onClick={() => {
          if (!atMax) {
            openMenu();
            inputRef.current?.focus();
          }
        }}
      >
        {selectedSkills.map((skill) => (
          <span
            key={skill.id}
            data-testid="selected-skill-chip"
            className="flex items-center gap-1 bg-pine-pine text-white rounded-full px-3 py-0.5 text-[12.5px] font-semibold"
          >
            {skill.title}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeChip(skill.id);
              }}
              className="ml-0.5 text-white/80 hover:text-white leading-none text-[14px]"
            >
              ×
            </button>
          </span>
        ))}
        {!atMax && (
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              openMenu();
            }}
            onFocus={() => openMenu()}
            onKeyDown={handleKeyDown}
            placeholder={selectedIds.length === 0 ? 'Search skills...' : ''}
            className="flex-1 min-w-[100px] text-[13px] bg-transparent outline-none"
          />
        )}
      </div>

      {error && <p className="text-[11px] text-pine-danger mt-1">{error}</p>}

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className="border-[1.5px] border-pine-line rounded-[9px] bg-white shadow-[0_4px_12px_rgba(0,0,0,0.08)] max-h-[200px] overflow-y-auto"
          >
            {Object.keys(filteredGroups).length === 0 ? (
              <p className="text-[13px] text-pine-ink-soft px-3 py-2">No skills found</p>
            ) : (
              Object.entries(filteredGroups).map(([domain, domainSkills]) => (
                <div key={domain}>
                  <p className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft bg-[#F4F9F6]">
                    {domain}
                  </p>
                  {domainSkills.map((skill) => {
                    const isSelected = selectedIds.includes(skill.id);
                    return (
                      <button
                        key={skill.id}
                        data-testid="skill-option"
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onClick={() => toggleSkill(skill.id)}
                        className={`w-full text-left px-5 py-2 text-[13px] transition-colors ${
                          isSelected
                            ? 'bg-pine-hover-item text-pine-pine font-medium'
                            : 'hover:bg-[#F4F9F6] text-pine-ink'
                        }`}
                      >
                        {skill.title}
                        {isSelected && <span className="ml-2 text-pine-pine">✓</span>}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
